import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { mobileRequest } from './api';
import { randomUuid } from './identifiers';

let listeners = [];
let cachedIdentity = null;
let registrationGeneration = 0;
let acceptingRegistrations = false;
let registrationQueue = Promise.resolve();
export const nativePushConfigured = () => Capacitor.isNativePlatform() && process.env.REACT_APP_MOBILE_PUSH_CONFIGURED === 'true';
export async function installationIdentity() {
  if (cachedIdentity) return cachedIdentity;
  const existing = await SecureStorage.get('portal-installation-v1');
  if (existing?.installation_id && existing?.installation_secret) { cachedIdentity = existing; return existing; }
  cachedIdentity = { installation_id: randomUuid(), installation_secret: `${randomUuid()}${randomUuid()}`, registered: false };
  await SecureStorage.set('portal-installation-v1', cachedIdentity);
  return cachedIdentity;
}
async function registerToken(value, enabled = true, generation = registrationGeneration) {
  const run = registrationQueue.catch(() => {}).then(async () => {
    if (!acceptingRegistrations || generation !== registrationGeneration) return;
    const identity = await installationIdentity();
    const info = await App.getInfo();
    if (!acceptingRegistrations || generation !== registrationGeneration) return;
    await mobileRequest('/devices/', { method: 'POST', body: JSON.stringify({ installation_id: identity.installation_id, installation_secret: identity.installation_secret, token: value, platform: Capacitor.getPlatform(), device_name: `${Capacitor.getPlatform() === 'ios' ? 'iPhone / iPad' : 'Android'} app`, app_version: info.version, push_enabled: enabled }) });
    cachedIdentity = { ...identity, registered: true };
    await SecureStorage.set('portal-installation-v1', cachedIdentity);
  });
  registrationQueue = run.catch(() => {});
  return run;
}
export async function enablePushNotifications() {
  if (!Capacitor.isNativePlatform()) throw new Error('Install the iPhone or Android app to enable push notifications.');
  if (!nativePushConfigured()) throw new Error('Push setup is pending. Add the Firebase app configuration and APNs credentials, then rebuild the app.');
  acceptingRegistrations = true;
  let permission = await FirebaseMessaging.checkPermissions();
  if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') permission = await FirebaseMessaging.requestPermissions();
  if (permission.receive !== 'granted') throw new Error('Notifications are disabled for this app. Enable them in your phone settings.');
  if (Capacitor.getPlatform() === 'android') await FirebaseMessaging.createChannel({ id: 'portal_updates', name: 'Case and task updates', description: 'Your task, document, appointment and filing updates', importance: 4, visibility: -1 });
  const result = await FirebaseMessaging.getToken();
  await registerToken(result.token);
  return 'Push notifications are enabled on this device.';
}
export async function initializePushListeners(onRoute, onRefresh) {
  if (!nativePushConfigured()) return () => {};
  acceptingRegistrations = true;
  const generation = ++registrationGeneration;
  await Promise.all(listeners.map(listener => listener.remove()));
  const ownedListeners = [
    await FirebaseMessaging.addListener('tokenReceived', async event => {
      try { await registerToken(event.token, true, generation); } catch { /* Re-register when the app resumes. */ }
    }),
    await FirebaseMessaging.addListener('notificationReceived', () => onRefresh()),
    await FirebaseMessaging.addListener('notificationActionPerformed', event => {
      const data = event.notification?.data || {};
      onRoute(data.route || data.deep_link, data.notification_id, data.account_type);
    }),
  ];
  if (generation !== registrationGeneration || !acceptingRegistrations) {
    await Promise.all(ownedListeners.map(listener => listener.remove()));
    return () => {};
  }
  listeners = ownedListeners;
  const permission = await FirebaseMessaging.checkPermissions();
  if (permission.receive === 'granted') {
    try { const result = await FirebaseMessaging.getToken(); await registerToken(result.token, true, generation); } catch { /* Inbox remains usable offline. */ }
  }
  return () => { ownedListeners.forEach(listener => listener.remove()); if (listeners === ownedListeners) listeners = []; };
}
export async function refreshPushRegistration() {
  if (!nativePushConfigured() || !acceptingRegistrations) return;
  const generation = registrationGeneration;
  try {
    const permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive !== 'granted') return;
    const result = await FirebaseMessaging.getToken();
    await registerToken(result.token, true, generation);
  } catch { /* Retried on the next foreground or connectivity event. */ }
}
export async function unregisterPushInstallation() {
  if (!Capacitor.isNativePlatform()) return;
  acceptingRegistrations = false;
  registrationGeneration += 1;
  await Promise.all(listeners.map(listener => listener.remove().catch(() => {})));
  listeners = [];
  await registrationQueue;
  const identity = await installationIdentity();
  if (identity.registered === false) return;
  // Retain the device proof across sign-outs so the next account can safely bind
  // the same installation, including when a previous sign-out happened offline.
  let unregisterError;
  try { await mobileRequest(`/devices/${identity.installation_id}/`, { method: 'DELETE' }); } catch (error) { if (error.status !== 404) unregisterError = error; }
  if (nativePushConfigured()) {
    try { await FirebaseMessaging.deleteToken(); unregisterError = null; } catch { /* See error below. */ }
    await FirebaseMessaging.removeAllDeliveredNotifications().catch(() => {});
  }
  if (unregisterError) throw new Error('Connect to the internet before signing out so this phone can stop receiving the current account’s notifications.');
  cachedIdentity = { ...identity, registered: false };
  await SecureStorage.set('portal-installation-v1', cachedIdentity);
}
